// =============================================================================
// Translation strings
// =============================================================================
// Every user-facing string in the app lives here. We use simple flat keys
// with dot-namespaced grouping (e.g., "profile.title", "auth.signIn").
//
// Adding a string:
//   1. Add the key with English and Tamil values below
//   2. Use it in a component: const t = useT(); ... t("my.new.key")
//
// Tamil translations were prepared with help from Anna University Tamil
// department staff (per the SOW reviewer guidance). Where the Tamil
// translation is intentionally identical to English (e.g., "Cashfree"),
// it's left as such.
//
// IMPORTANT: keep this file alphabetical within each section. Easier to
// grep when adding a new string.
// =============================================================================

export const strings = {
  // ===========================================================================
  // Common
  // ===========================================================================
  "common.tickAndPass": { en: "TickAndPass", ta: "TickAndPass" },
  "common.cancel": { en: "Cancel", ta: "ரத்து செய்" },
  "common.save": { en: "Save", ta: "சேமி" },
  "common.continue": { en: "Continue", ta: "தொடரவும்" },
  "common.submit": { en: "Submit", ta: "சமர்ப்பி" },
  "common.back": { en: "Back", ta: "பின்செல்" },
  "common.next": { en: "Next", ta: "அடுத்து" },
  "common.confirm": { en: "Confirm", ta: "உறுதிப்படுத்து" },
  "common.delete": { en: "Delete", ta: "நீக்கு" },
  "common.edit": { en: "Edit", ta: "திருத்து" },
  "common.loading": { en: "Loading...", ta: "ஏற்றுகிறது..." },
  "common.signIn": { en: "Sign in", ta: "உள்நுழை" },
  "common.signOut": { en: "Sign out", ta: "வெளியேறு" },
  "common.signUp": { en: "Sign up", ta: "பதிவு செய்" },
  "common.required": { en: "Required", ta: "அவசியம்" },
  "common.optional": { en: "Optional", ta: "விருப்பப்படி" },
  "common.searchPlaceholder": { en: "Search...", ta: "தேடு..." },
  "common.email": { en: "Email", ta: "மின்னஞ்சல்" },
  "common.password": { en: "Password", ta: "கடவுச்சொல்" },
  "common.notFound": { en: "Not found", ta: "காணப்படவில்லை" },
  "common.somethingWrong": {
    en: "Something went wrong. Please try again.",
    ta: "ஏதோ தவறு நடந்தது. மீண்டும் முயற்சிக்கவும்.",
  },

  // ===========================================================================
  // Public site chrome (tabs strip + header corner links) & coming-soon pages
  // ===========================================================================
  "pub.shopTab": { en: "Shop", ta: "கடை" },
  "pub.gatewayTab": { en: "Talent Hub", ta: "திறமையாளர்கள் மையம்" },
  "pub.loginTab": { en: "User login", ta: "பயனர் உள்நுழைவு" },
  "pub.forBusinesses": { en: "For Employers", ta: "பணியளிப்பாளர்களுக்கு" },
  "pub.forInstitutions": {
    en: "For Academic Institutions",
    ta: "கல்வி நிறுவனங்களுக்கு",
  },
  "pub.termsAndConditions": {
    en: "Terms & Conditions",
    ta: "விதிகள் மற்றும் நிபந்தனைகள்",
  },
  "pub.privacyPolicy": { en: "Privacy Policy", ta: "தனியுரிமைக் கொள்கை" },
  "pub.terms": { en: "Terms & Conditions", ta: "விதிமுறைகள் & நிபந்தனைகள்" },
  "pub.privacy": { en: "Privacy Policy", ta: "தனியுரிமைக் கொள்கை" },
  "pub.bizMarqueeTitle": {
    en: "Built for hiring teams across India",
    ta: "இந்தியா முழுவதும் பணியமர்த்தும் குழுக்களுக்காக",
  },
  "pub.eduMarqueeTitle": {
    en: "Built for institutions issuing credentials",
    ta: "சான்றுகளை வழங்கும் கல்வி நிறுவனங்களுக்காக",
  },
  "pub.partnersComing": {
    en: "Partner announcements coming soon. Onboarding is underway.",
    ta: "கூட்டாளர் அறிவிப்புகள் விரைவில் - இணைப்புப் பணிகள் நடைபெறுகின்றன.",
  },

  "coming.title": { en: "Coming soon...", ta: "விரைவில் வருகிறது..." },
  "coming.shopTitle": { en: "The Tick & Pass Shop", ta: "Tick & Pass கடை" },
  "coming.gatewayTitle": { en: "Talent Hub", ta: "திறமையாளர்கள் மையம்" },
  "coming.authTitle": { en: "Sign in & sign up", ta: "உள்நுழைவு & பதிவு" },
  "coming.termsTitle": {
    en: "Terms & Conditions",
    ta: "விதிமுறைகள் & நிபந்தனைகள்",
  },
  "coming.privacyTitle": { en: "Privacy Policy", ta: "தனியுரிமைக் கொள்கை" },
  "coming.body": {
    en: "We're working on something exciting. Check back soon!",
    ta: "சுவாரஸ்யமான ஒன்றை உருவாக்கி வருகிறோம். விரைவில் மீண்டும் பாருங்கள்!",
  },
  "coming.offerBody": {
    en: "Special offers for this community are on the way.",
    ta: "இந்தச் சமூகத்திற்கான சிறப்புச் சலுகைகள் வழியில் உள்ளன.",
  },
  "coming.back": { en: "Back to home", ta: "முகப்புக்குத் திரும்பு" },
  "coming.eligible": {
    en: "You're on the list. Offers for your community will reach you first",
    ta: "நீங்கள் பட்டியலில் உள்ளீர்கள்! உங்கள் சமூகத்திற்கான சலுகைகள் உங்களை முதலில் அடையும்",
  },

  // ===========================================================================
  // Brand tagline (tri-coloured segments in the logo lockup)
  // ===========================================================================
  "brand.taglinePart1": {
    en: "Verified once.",
    ta: "ஒருமுறை சரிபார்க்கப்பட்டது.",
  },
  "brand.taglinePart2": { en: "At source.", ta: "மூலத்திலேயே." },
  "brand.taglinePart3": {
    en: "Trusted everywhere, every time.",
    ta: "எங்கும், எப்போதும் நம்பப்படும்.",
  },

  // ===========================================================================
  // Landing page
  // ===========================================================================
  // Hero -- stacked three-line headline (ID.me style), tagline underneath
  "landing.heroLine1": { en: "Get verified.", ta: "சரிபார்ப்பு பெறுங்கள்." },
  "landing.heroLine2": {
    en: "Build trust.",
    ta: "நம்பிக்கையை உருவாக்குங்கள்.",
  },
  "landing.heroLine3": { en: "Sign up now!", ta: "இப்போதே பதிவு செய்யுங்கள்!" },
  "landing.heroTitle": {
    en: "Get verified. Build trust. Sign up now!",
    ta: "சரிபார்ப்பு பெறுங்கள். நம்பிக்கையை உருவாக்குங்கள். இப்போதே பதிவு செய்யுங்கள்!",
  },
  "landing.tagline": {
    en: "Verified once, at source. Trusted everywhere, every time.",
    ta: "ஒருமுறை மூலத்தில் சரிபார்க்கப்பட்டது - எங்கும், எப்போதும் நம்பப்படும்.",
  },
  "landing.heroSubtitle": {
    en: "Verified once, at source. Trusted everywhere, every time.",
    ta: "ஒருமுறை மூலத்தில் சரிபார்க்கப்பட்டது - எங்கும், எப்போதும் நம்பப்படும்.",
  },

  // Profession-specific benefits grid (below the hero) -- 24-07 client copy.
  // Community names are alphabetical and must stay in sync with the backend
  // DTO allow-list and Register.jsx COMMUNITIES.
  "landing.offersTitle": {
    en: "Discover Profession-specific Benefits on Tick and Pass",
    ta: "Tick and Pass-இல் தொழில்சார் சலுகைகளைக் கண்டறியுங்கள்",
  },
  "landing.offersSubtitle": {
    en: "Enjoy exclusive deals and savings!",
    ta: "பிரத்யேக சலுகைகளையும் சேமிப்புகளையும் அனுபவியுங்கள்!",
  },
  "landing.offStudents": { en: "Students", ta: "மாணவர்கள்" },
  "landing.offDefence": {
    en: "Defence Personnel",
    ta: "பாதுகாப்புப் படையினர்",
  },
  "landing.offPolice": {
    en: "Police & Uniformed Services",
    ta: "காவல் & சீருடைப் பணிகள்",
  },
  "landing.offTeachers": {
    en: "Teaching Professionals",
    ta: "ஆசிரியப் பணியாளர்கள்",
  },
  "landing.offMedical": { en: "Doctors", ta: "மருத்துவர்கள்" },
  "landing.offNurses": {
    en: "Nurses & Allied Health Professionals",
    ta: "செவிலியர்கள் & துணை சுகாதாரப் பணியாளர்கள்",
  },
  "landing.offResponders": { en: "First Responders", ta: "முதல் மீட்பவர்கள்" },
  "landing.offGovt": { en: "Government Employees", ta: "அரசு ஊழியர்கள்" },

  // Navy "get started" band (three signup cards)
  "landing.joinTitle": {
    en: "Get started with Tick & Pass",
    ta: "Tick & Pass உடன் தொடங்குங்கள்",
  },
  "landing.joinSubtitle": {
    en: "One verified profile that works everywhere.",
    ta: "எங்கும் செல்லுபடியாகும் ஒரே சரிபார்க்கப்பட்ட சுயவிவரம்.",
  },

  // "Explore benefits by community" tiles
  "landing.communityTitle": {
    en: "Explore benefits of signing up with Tick and Pass",
    ta: "Tick and Pass-இல் பதிவு செய்வதன் பயன்களை அறியுங்கள்",
  },
  "landing.communitySubtitle": {
    en: "Enabling credentials to be verified at source!",
    ta: "சான்றுகளை மூலத்திலேயே சரிபார்க்க வழி செய்கிறோம்!",
  },
  "landing.commProfessionals": {
    en: "Professionals",
    ta: "தொழில் வல்லுநர்கள்",
  },
  "landing.commProfessionalsDesc": {
    en: "Verify once and carry your credentials with you throughout your career.",
    ta: "ஒருமுறை சரிபார்த்து, உங்கள் தொழில் பயணம் முழுவதும் சான்றுகளை உடன் கொண்டு செல்லுங்கள்.",
  },
  "landing.commEmployers": { en: "Employers", ta: "பணியளிப்பாளர்கள்" },
  "landing.commEmployersDesc": {
    en: "Hire faster with source-verified candidates, without repeat background checks.",
    ta: "மூலத்தில் சரிபார்க்கப்பட்ட வேட்பாளர்களுடன் விரைவாக பணியமர்த்துங்கள் - மீண்டும் பின்னணிச் சரிபார்ப்பு தேவையில்லை.",
  },
  "landing.commInstitutions": {
    en: "Academic Institutions",
    ta: "கல்வி நிறுவனங்கள்",
  },
  "landing.commInstitutionsDesc": {
    en: "Verify credentials once at the source and end repeat verification requests.",
    ta: "சான்றுகளை ஒருமுறை மூலத்தில் சரிபாருங்கள்; மீண்டும் மீண்டும் வரும் சரிபார்ப்புக் கோரிக்கைகளை நிறுத்துங்கள்.",
  },

  // "For businesses" split section
  "landing.bizEyebrow": { en: "For Employers", ta: "பணியளிப்பாளர்களுக்கு" },
  "landing.bizHeadline": {
    en: "Enable source-verified credentials. Empower every transition with credibility.",
    ta: "மூலத்தில் சரிபார்க்கப்பட்ட சான்றுகளை இயக்குங்கள். ஒவ்வொரு மாற்றத்திற்கும் நம்பகத்தன்மை சேருங்கள்.",
  },
  "landing.bizBody1": {
    en: "Professionals routinely submit their academic qualifications and employment history whenever they apply for a new role. Yet, despite these credentials often remaining unchanged, employers are required to verify them from scratch every time a candidate joins a new organisation. This repetitive process is time consuming, costly, and can sometimes create unnecessary friction for both employers and professionals.",
    ta: "தொழில் வல்லுநர்கள் புதிய வேலைக்கு விண்ணப்பிக்கும் ஒவ்வொரு முறையும் தங்கள் கல்வித் தகுதிகளையும் பணி வரலாற்றையும் சமர்ப்பிக்கிறார்கள். இந்தச் சான்றுகள் பெரும்பாலும் மாறாதவை என்றாலும், ஒவ்வொரு புதிய நிறுவனத்திலும் அவற்றை முதலில் இருந்து சரிபார்க்க வேண்டியுள்ளது. இந்த மீண்டும் மீண்டும் நடக்கும் செயல்முறை நேரத்தை விழுங்குகிறது, செலவு மிக்கது, சில நேரங்களில் பணியளிப்பாளர்களுக்கும் தொழில் வல்லுநர்களுக்கும் தேவையற்ற தடைகளையும் உருவாக்கக்கூடும்.",
  },
  "landing.bizBody2": {
    en: "Tick & Pass solves this problem. It puts users in control of their own verified credentials. Instead of undergoing repeated background checks throughout their careers, individuals verify their academic records, employment history, and other credentials once, directly at the source. These tamper-proof, source-verified credentials then travel securely with them throughout their professional journey.",
    ta: "Tick & Pass இந்தச் சிக்கலைத் தீர்க்கிறது. பயனர்கள் தங்கள் சரிபார்க்கப்பட்ட சான்றுகளின் கட்டுப்பாட்டைத் தாங்களே பெறுகிறார்கள். தொழில் வாழ்க்கை முழுவதும் மீண்டும் மீண்டும் பின்னணிச் சரிபார்ப்புகளைச் சந்திப்பதற்குப் பதிலாக, கல்விப் பதிவுகள், பணி வரலாறு போன்றவற்றை ஒருமுறை நேரடியாக மூலத்திலேயே சரிபார்க்கிறார்கள். மாற்ற முடியாத, மூலத்தில் சரிபார்க்கப்பட்ட இந்தச் சான்றுகள் அவர்களுடன் பாதுகாப்பாகப் பயணிக்கின்றன.",
  },
  "landing.bizBody3": {
    en: "This user-centric approach strips unnecessary friction out of hiring and onboarding workflows, giving professionals and employers alike a smooth transition, every time.",
    ta: "இந்த பயனர் மைய அணுகுமுறை பணியமர்த்தல் மற்றும் சேர்க்கை செயல்முறைகளில் உள்ள தேவையற்ற தடைகளை நீக்கி, தொழில் வல்லுநர்களுக்கும் பணியளிப்பாளர்களுக்கும் ஒவ்வொரு முறையும் சீரான அனுபவத்தை அளிக்கிறது.",
  },

  // "For academic institutions" split section
  "landing.eduEyebrow": {
    en: "For academic institutions",
    ta: "கல்வி நிறுவனங்களுக்கு",
  },
  "landing.eduHeadline": {
    en: "Enable source-verified credentials. Empower every student's transition journey.",
    ta: "மூலத்தில் சரிபார்க்கப்பட்ட சான்றுகளை இயக்குங்கள். ஒவ்வொரு மாணவரின் மாற்றப் பயணத்திற்கும் வலு சேருங்கள்.",
  },
  "landing.eduBody1": {
    en: "Every year, academic institutions issue thousands of degrees, diplomas, transcripts, and certificates to graduates. Yet long after students leave campus, institutions continue to receive countless requests from employers, universities, and verification agencies to validate the same credentials repeatedly. This manual verification process consumes valuable administrative time, increases operational costs, and delays opportunities for graduates.",
    ta: "ஒவ்வொரு ஆண்டும் கல்வி நிறுவனங்கள் ஆயிரக்கணக்கான பட்டங்கள், டிப்ளமோக்கள், மதிப்பெண் பட்டியல்கள் மற்றும் சான்றிதழ்களை வழங்குகின்றன. மாணவர்கள் வளாகத்தை விட்டு வெளியேறிய பின்னரும், அதே சான்றுகளை மீண்டும் மீண்டும் உறுதிப்படுத்துமாறு எண்ணற்ற கோரிக்கைகள் வந்துகொண்டே இருக்கின்றன. இந்த கைமுறை செயல்முறை நிர்வாக நேரத்தை விழுங்கி, செலவுகளை அதிகரித்து, பட்டதாரிகளின் வாய்ப்புகளைத் தாமதப்படுத்துகிறது.",
  },
  "landing.eduBody2": {
    en: "Tick & Pass reimagines this process by enabling users to get their academic credentials verified once, at the source. These digitally verified, tamper-proof credentials then travel securely with them throughout their academic and professional journey.",
    ta: "Tick & Pass இந்த செயல்முறையை மறுவடிவமைக்கிறது: பயனர்கள் தங்கள் கல்விச் சான்றுகளை ஒருமுறை மூலத்திலேயே சரிபார்த்துக் கொள்கிறார்கள். டிஜிட்டல் முறையில் சரிபார்க்கப்பட்ட, மாற்ற முடியாத இந்தச் சான்றுகள் அவர்களின் கல்வி மற்றும் தொழில் பயணம் முழுவதும் பாதுகாப்பாக உடன் செல்கின்றன.",
  },
  "landing.eduBody3": {
    en: "Whenever graduates apply for employment or higher education, they can instantly share their source-verified credentials without requiring institutions to respond to repeated verification requests. This reduces administrative workload, strengthens trust in institutional records, accelerates verification, and creates a seamless experience for institutions, graduates, employers, and higher-education providers alike.",
    ta: "பட்டதாரிகள் வேலை அல்லது மேற்படிப்புக்கு விண்ணப்பிக்கும் போது, மூலத்தில் சரிபார்க்கப்பட்ட சான்றுகளை உடனடியாகப் பகிரலாம் - நிறுவனங்கள் மீண்டும் மீண்டும் பதிலளிக்கத் தேவையில்லை. நிர்வாகச் சுமை குறைகிறது, நிறுவனப் பதிவுகளின் மீதான நம்பிக்கை வலுப்பெறுகிறது, சரிபார்ப்பு விரைவாகிறது, அனைவருக்கும் சீரான அனுபவம் கிடைக்கிறது.",
  },

  // Navy impact-stats band
  "landing.impactTitle": {
    en: "Making an impact during transition periods!",
    ta: "மாற்றக் காலங்களில் தாக்கம் ஏற்படுத்துகிறோம்!",
  },
  "landing.impactMembersCount": { en: "1,000+", ta: "1,000+" },
  "landing.impactMembersLabel": { en: "members", ta: "உறுப்பினர்கள்" },
  "landing.impactMembersDesc": {
    en: "professionals building source-verified profiles",
    ta: "மூலத்தில் சரிபார்க்கப்பட்ட சுயவிவரங்களை உருவாக்கும் தொழில் வல்லுநர்கள்",
  },
  "landing.impactOrgsCount": { en: "100+", ta: "100+" },
  "landing.impactOrgsLabel": { en: "organisations", ta: "நிறுவனங்கள்" },
  "landing.impactOrgsDesc": {
    en: "employers hiring with trusted credentials",
    ta: "நம்பகமான சான்றுகளுடன் பணியமர்த்தும் பணியளிப்பாளர்கள்",
  },
  "landing.impactInstCount": { en: "50+", ta: "50+" },
  "landing.impactInstLabel": {
    en: "academic institutions",
    ta: "கல்வி நிறுவனங்கள்",
  },
  "landing.impactInstDesc": {
    en: "verifying credentials directly at the source",
    ta: "மூலத்திலேயே சான்றுகளைச் சரிபார்க்கும் நிறுவனங்கள்",
  },

  // Bottom CTA band
  "landing.bottomCtaTitle": {
    en: "Keep your professional journey verified with Tick & Pass",
    ta: "Tick & Pass உடன் உங்கள் தொழில் பயணத்தை சரிபார்க்கப்பட்டதாக வைத்திருங்கள்",
  },
  "landing.bottomCtaBtn": {
    en: "Sign up or create account",
    ta: "பதிவு செய்யுங்கள் அல்லது கணக்கை உருவாக்குங்கள்",
  },

  "landing.ctaProfessional": {
    en: "I'm an individual",
    ta: "நான் ஒரு தனிநபர்",
  },
  "landing.ctaProfessionalDesc": {
    en: "Build a verified profile of your education and work. Share with confidence.",
    ta: "உங்கள் கல்வி மற்றும் பணியின் சரிபார்க்கப்பட்ட சுயவிவரத்தை உருவாக்குங்கள்.",
  },
  "landing.ctaProfessionalBtn": { en: "Sign up", ta: "பதிவு செய்" },
  "landing.ctaProfessionalPrice": {
    en: "From Rs. 180/year",
    ta: "ஆண்டுக்கு ரூ. 180 முதல்",
  },

  "landing.ctaInstitution": {
    en: "I'm an academic institution",
    ta: "நான் ஒரு கல்வி நிறுவனம்",
  },
  "landing.ctaInstitutionDesc": {
    en: "Verify your alumni and students.",
    ta: "உங்கள் முன்னாள் மாணவர்களையும் மாணவர்களையும் சரிபார்க்கவும்.",
  },
  "landing.ctaInstitutionBtn": {
    en: "Sign up as Institution",
    ta: "நிறுவனமாக பதிவு செய்",
  },
  "landing.ctaInstitutionPrice": {
    en: "{price}/year",
    ta: "ஆண்டுக்கு {price}",
  },

  "landing.ctaOrg": { en: "I'm an employer", ta: "நான் ஒரு பணியளிப்பாளர்" },
  "landing.ctaOrgDesc": {
    en: "Verify your former employees. Discover and engage verified candidates.",
    ta: "முன்னாள் ஊழியர்களை சரிபார்க்கவும். சரிபார்க்கப்பட்ட வேட்பாளர்களைக் கண்டறியுங்கள்.",
  },
  "landing.ctaOrgBtn": {
    en: "Sign up as Organisation",
    ta: "நிறுவனமாக பதிவு செய்",
  },
  "landing.ctaOrgPrice": { en: "{price}/year", ta: "ஆண்டுக்கு {price}" },

  // Aliases used by Landing.jsx (ctaOrganisation* matches variable names)
  "landing.ctaOrganisation": {
    en: "I'm an employer",
    ta: "நான் ஒரு பணியளிப்பாளர்",
  },
  "landing.ctaOrganisationDesc": {
    en: "Verify your former employees. Discover and engage verified candidates.",
    ta: "முன்னாள் ஊழியர்களை சரிபார்க்கவும். சரிபார்க்கப்பட்ட வேட்பாளர்களைக் கண்டறியுங்கள்.",
  },
  "landing.ctaOrganisationBtn": {
    en: "Sign up as Organisation",
    ta: "நிறுவனமாக பதிவு செய்",
  },
  "landing.ctaOrganisationPrice": {
    en: "{price}/year",
    ta: "ஆண்டுக்கு {price}",
  },

  // Feature section (border-top accent columns)
  "landing.feat1Title": {
    en: "Verified by the source",
    ta: "மூலத்தால் சரிபார்க்கப்பட்டது",
  },
  "landing.feat1Body": {
    en: "Every credential is confirmed directly by the institution or employer that issued it. Only verified institutions and employers are onboarded.",
    ta: "ஒவ்வொரு சான்றும் வழங்கிய நிறுவனத்தால் நேரடியாக உறுதிப்படுத்தப்படுகிறது. சரிபார்க்கப்பட்ட நிறுவனங்கள் மட்டுமே சேர்க்கப்படுகின்றன.",
  },
  "landing.feat2Title": { en: "A partnered model", ta: "கூட்டாண்மை மாதிரி" },
  "landing.feat2Body": {
    en: "Institutions and employers partner with TickAndPass to build a sustainable model that incentivises accuracy.",
    ta: "கல்வி நிறுவனங்கள் மற்றும் பணியளிப்பாளர்கள் TickAndPass உடன் கூட்டாக துல்லியத்தை ஊக்குவிக்கும் ஒரு நிலையான மாதிரியை உருவாக்குகின்றனர்.",
  },
  "landing.feat3Title": { en: "Private by design", ta: "தனியுரிமை முதல்" },
  "landing.feat3Body": {
    en: "Professionals control who sees their profile. Share only verified credentials, only with who you choose.",
    ta: "தொழில் வல்லுநர்கள் தங்கள் சுயவிவரத்தை யார் பார்க்கலாம் என்று கட்டுப்படுத்துகிறார்கள்.",
  },

  // Profile card preview labels (hero right column)
  "landing.education": { en: "Education", ta: "கல்வி" },
  "landing.experience": { en: "Experience", ta: "பணி அனுபவம்" },

  "landing.alreadyHaveAccount": {
    en: "Already have an account?",
    ta: "ஏற்கனவே கணக்கு உள்ளதா?",
  },
  "landing.trustVerifiedCount": {
    en: "{n} verified credentials and counting",
    ta: "{n} சரிபார்க்கப்பட்ட சான்றுகள், மேலும் சேர்க்கப்பட்டு வருகின்றன",
  },

  // ===========================================================================
  // Auth
  // ===========================================================================
  "auth.welcomeBack": { en: "Welcome back", ta: "மீண்டும் வரவேற்கிறோம்" },
  "auth.signInToContinue": {
    en: "Sign in to your TickAndPass account",
    ta: "உங்கள் TickAndPass கணக்கில் உள்நுழைக",
  },
  "auth.signingIn": { en: "Signing in...", ta: "உள்நுழைகிறது..." },
  "auth.newToTickAndPass": {
    en: "New to TickAndPass?",
    ta: "TickAndPass-க்கு புதியவரா?",
  },
  "auth.createAccount": { en: "Create an account", ta: "கணக்கை உருவாக்கு" },
  "auth.firstName": { en: "First name", ta: "முதல் பெயர்" },
  "auth.lastName": { en: "Last name", ta: "கடைசி பெயர்" },
  "auth.passwordHint": {
    en: "At least 8 characters",
    ta: "குறைந்தது 8 எழுத்துக்கள்",
  },
  "auth.aadhaar": { en: "Aadhaar number", ta: "ஆதார் எண்" },
  "auth.aadhaarHint": {
    en: "12 digits. Stored securely, never shown publicly.",
    ta: "12 இலக்கங்கள். பாதுகாப்பாக சேமிக்கப்படும்; பொதுவில் காட்டப்படாது.",
  },
  "auth.pan": { en: "PAN (optional)", ta: "PAN (விருப்பப்படி)" },
  "auth.panHint": { en: "Format: ABCDE1234F", ta: "வடிவம்: ABCDE1234F" },
  "auth.community": { en: "Community (optional)", ta: "சமூகம் (விருப்பப்படி)" },
  "auth.communityHint": {
    en: "Be first in line when offers for your community launch.",
    ta: "உங்கள் சமூகத்திற்கான சலுகைகள் வரும்போது முதலில் பெறுங்கள்.",
  },
  "auth.welcomeAboard": {
    en: "Welcome aboard!",
    ta: "இணைந்ததற்கு வரவேற்கிறோம்!",
  },
  "auth.verificationEmailSent": {
    en: "We've sent a verification email to {email}. Verify your email to unlock the full platform, but you can start building your profile right away.",
    ta: "சரிபார்ப்பு மின்னஞ்சல் {email}-க்கு அனுப்பப்பட்டுள்ளது.",
  },
  "auth.continueToProfile": {
    en: "Continue to my profile",
    ta: "சுயவிவரத்திற்குச் செல்லவும்",
  },

  // ===========================================================================
  // Profile page
  // ===========================================================================
  "profile.title": { en: "Profile", ta: "சுயவிவரம்" },
  "profile.education": { en: "Education", ta: "கல்வி" },
  "profile.workExperience": { en: "Work experience", ta: "பணி அனுபவம்" },
  "profile.others": { en: "Others", ta: "மற்றவை" },
  "profile.verifiedCredentials": {
    en: "Verified credentials",
    ta: "சரிபார்க்கப்பட்ட சான்றுகள்",
  },
  "profile.verifiedOf": {
    en: "{verified} of {total} verified",
    ta: "{total}-இல் {verified} சரிபார்க்கப்பட்டது",
  },
  "profile.addEducation": { en: "+ Add education", ta: "+ கல்வி சேர்" },
  "profile.addWork": { en: "+ Add work", ta: "+ பணி சேர்" },
  "profile.addCredential": { en: "+ Add credential", ta: "+ சான்று சேர்" },
  "profile.requestVerification": {
    en: "Request verification",
    ta: "சரிபார்ப்பு கோரு",
  },
  "profile.emptyEducation": {
    en: "No education entries yet.",
    ta: "இன்னும் கல்வி பதிவுகள் இல்லை.",
  },
  "profile.emptyWork": {
    en: "No work experience yet.",
    ta: "இன்னும் பணி அனுபவம் இல்லை.",
  },
  "profile.emptyOther": {
    en: "Short courses, certifications, or licences.",
    ta: "குறுகிய பாடநெறிகள், சான்றிதழ்கள் அல்லது உரிமங்கள்.",
  },
  "profile.addOne": { en: "Add one", ta: "ஒன்றைச் சேர்" },
  "profile.emailNotVerified": {
    en: "Email not verified",
    ta: "மின்னஞ்சல் சரிபார்க்கப்படவில்லை",
  },
  "profile.student": { en: "Student", ta: "மாணவர்" },
  "profile.professional": { en: "Professional", ta: "தொழில்முறையாளர்" },
  "profile.shareMyProfile": {
    en: "Share my profile",
    ta: "என் சுயவிவரத்தைப் பகிர்",
  },
  "profile.shareLink": { en: "Share link", ta: "பகிர்வு இணைப்பு" },
  "profile.copyLink": { en: "Copy link", ta: "இணைப்பை நகலெடு" },
  "profile.linkCopied": {
    en: "Link copied!",
    ta: "இணைப்பு நகலெடுக்கப்பட்டது!",
  },

  // ===========================================================================
  // Status badges
  // ===========================================================================
  "status.unverified": { en: "Unverified", ta: "சரிபார்க்கப்படவில்லை" },
  "status.pending": { en: "Pending", ta: "நிலுவையில்" },
  "status.verified": { en: "Verified", ta: "சரிபார்க்கப்பட்டது" },
  "status.withdrawn": { en: "Withdrawn", ta: "திரும்பப் பெறப்பட்டது" },
  "status.rejected": { en: "Rejected", ta: "நிராகரிக்கப்பட்டது" },
  "status.approved": { en: "Approved", ta: "அங்கீகரிக்கப்பட்டது" },
  "status.suspended": { en: "Suspended", ta: "இடைநீக்கப்பட்டது" },
  "status.pendingReview": { en: "Pending review", ta: "ஆய்வுக்கு நிலுவையில்" },

  // ===========================================================================
  // Verification flow
  // ===========================================================================
  "verify.title": { en: "Request verification", ta: "சரிபார்ப்பு கோரு" },
  "verify.subtitle": {
    en: "A verifier reviews your credential and, if approved, marks it as verified on your profile.",
    ta: "ஒரு சரிபார்ப்பாளர் உங்கள் சான்றை ஆய்வு செய்கிறார்.",
  },
  "verify.credentialToVerify": {
    en: "Credential to verify",
    ta: "சரிபார்க்க வேண்டிய சான்று",
  },
  "verify.chooseCredential": {
    en: "Choose a credential...",
    ta: "ஒரு சான்றைத் தேர்வு செய்...",
  },
  "verify.verifierType": { en: "Verifier type", ta: "சரிபார்ப்பாளர் வகை" },
  "verify.academicInstitution": {
    en: "Academic institution",
    ta: "கல்வி நிறுவனம்",
  },
  "verify.academicInstitutionHint": {
    en: "College, university, school",
    ta: "கல்லூரி, பல்கலைக்கழகம், பள்ளி",
  },
  "verify.organisation": { en: "Organisation", ta: "நிறுவனம்" },
  "verify.organisationHint": {
    en: "Past employer, company",
    ta: "முன்னாள் பணியளிப்பாளர், நிறுவனம்",
  },
  "verify.whichInstitution": {
    en: "Which institution?",
    ta: "எந்த கல்வி நிறுவனம்?",
  },
  "verify.whichOrganisation": {
    en: "Which organisation?",
    ta: "எந்த நிறுவனம்?",
  },
  "verify.searchInstitution": {
    en: "Search by name, city, or registration number",
    ta: "பெயர், நகரம், அல்லது பதிவு எண் மூலம் தேடு",
  },
  "verify.noResultsFound": { en: "No results found", ta: "முடிவுகள் இல்லை" },
  "verify.noVerifiersAvailable": {
    en: "No verifiers registered yet. Invite your institution or employer to join TickAndPass.",
    ta: "இன்னும் சரிபார்ப்பாளர்கள் இல்லை.",
  },
  "verify.noteToVerifier": {
    en: "Note to the verifier (optional)",
    ta: "சரிபார்ப்பாளருக்கு குறிப்பு (விருப்பப்படி)",
  },
  "verify.noteHint": {
    en: "The verifier sees this note alongside your credential. Keep it short.",
    ta: "சரிபார்ப்பாளர் இந்த குறிப்பை உங்கள் சான்றுடன் பார்ப்பார். சுருக்கமாக வைக்கவும்.",
  },
  "verify.verificationFee": {
    en: "Verification fee",
    ta: "சரிபார்ப்பு கட்டணம்",
  },
  "verify.feeBreakdownLabel": {
    en: "Rs. 375 to platform, Rs. 125 to verifier.",
    ta: "ரூ. 375 தளத்திற்கு, ரூ. 125 சரிபார்ப்பாளருக்கு.",
  },
  "verify.payAndSubmit": {
    en: "Pay {amount} and submit",
    ta: "{amount} செலுத்தி சமர்ப்பி",
  },
  "verify.openingCheckout": {
    en: "Opening checkout...",
    ta: "செலுத்துகை திறக்கிறது...",
  },

  "verify.myVerifications": {
    en: "My verifications",
    ta: "என் சரிபார்ப்புகள்",
  },
  "verify.requestNew": {
    en: "+ Request verification",
    ta: "+ சரிபார்ப்பு கோரு",
  },
  "verify.successBanner": {
    en: "Payment received. Your request is now in the verifier's queue.",
    ta: "செலுத்துகை பெறப்பட்டது. உங்கள் கோரிக்கை வரிசையில் உள்ளது.",
  },
  "verify.noRequestsYet": {
    en: "You haven't requested any verifications yet.",
    ta: "இன்னும் எந்த சரிபார்ப்பும் கோரப்படவில்லை.",
  },
  "verify.rejectionReasonLabel": {
    en: "Rejection reason:",
    ta: "நிராகரிப்புக்கான காரணம்:",
  },
  "verify.editContextLabel": {
    en: "Edit request - proposed changes to a verified credential",
    ta: "சரிபார்க்கப்பட்ட சான்றுக்கு திருத்தம் கோரிக்கை",
  },

  // Officer side (verify request screen)
  "verify.yourDecision": { en: "Your decision", ta: "உங்கள் முடிவு" },
  "verify.approve": { en: "Approve", ta: "அங்கீகரி" },
  "verify.reject": { en: "Reject", ta: "நிராகரி" },
  "verify.approveTitle": {
    en: "Approve this verification",
    ta: "இந்த சரிபார்ப்பை அங்கீகரி",
  },
  "verify.approveBody": {
    en: "You're confirming that this credential is genuine. The Professional's profile will show it as verified.",
    ta: "இந்த சான்று உண்மையானது என்பதை நீங்கள் உறுதிப்படுத்துகிறீர்கள்.",
  },
  "verify.approveNote": {
    en: "Approval note (optional)",
    ta: "அங்கீகரிப்பு குறிப்பு (விருப்பப்படி)",
  },
  "verify.confirmApproval": {
    en: "Confirm approval",
    ta: "அங்கீகாரத்தை உறுதிப்படுத்து",
  },
  "verify.rejectTitle": {
    en: "Reject this verification",
    ta: "இந்த சரிபார்ப்பை நிராகரி",
  },
  "verify.rejectBody": {
    en: "Explain why. The Professional sees this reason and can submit a new request after addressing it.",
    ta: "காரணத்தைக் கூறுங்கள். தொழில்முறையாளர் இதைப் பார்ப்பார்.",
  },
  "verify.rejectReasonLabel": {
    en: "Reason for rejection (required)",
    ta: "நிராகரிப்புக்கான காரணம் (அவசியம்)",
  },
  "verify.confirmRejection": {
    en: "Confirm rejection",
    ta: "நிராகரிப்பை உறுதிப்படுத்து",
  },
  "verify.viewCertificate": {
    en: "View certificate",
    ta: "சான்றிதழைப் பார்க்க",
  },
  "verify.attachedCertificate": {
    en: "Attached certificate",
    ta: "இணைக்கப்பட்ட சான்றிதழ்",
  },
  "verify.requestNote": {
    en: "Note from the requester:",
    ta: "கோரிக்கையாளரிடமிருந்து குறிப்பு:",
  },
  "verify.submittedBy": { en: "Submitted by", ta: "சமர்ப்பித்தவர்" },
  "verify.decisionReason": { en: "Decision reason", ta: "முடிவின் காரணம்" },

  // ===========================================================================
  // Dashboard (verifier)
  // ===========================================================================
  "dashboard.title": { en: "Home", ta: "முகப்பு" },
  "dashboard.pendingRequests": {
    en: "Pending requests",
    ta: "நிலுவையிலுள்ள கோரிக்கைகள்",
  },
  "dashboard.verifiedToDate": {
    en: "Verified to date",
    ta: "இதுவரை சரிபார்க்கப்பட்டவை",
  },
  "dashboard.activeOfficers": {
    en: "Active officers",
    ta: "செயலில் உள்ள அதிகாரிகள்",
  },
  "dashboard.recentActivity": {
    en: "Recent activity",
    ta: "சமீபத்திய செயல்பாடு",
  },
  "dashboard.noPending": {
    en: "No pending requests right now.",
    ta: "இப்போது நிலுவையில் கோரிக்கைகள் இல்லை.",
  },
  "dashboard.review": { en: "Review", ta: "ஆய்வு செய்" },
  "dashboard.viewAll": { en: "View all", ta: "அனைத்தையும் பார்க்க" },
  "dashboard.tabPending": { en: "Pending", ta: "நிலுவையில்" },
  "dashboard.tabVerified": { en: "Verified", ta: "சரிபார்க்கப்பட்டது" },
  "dashboard.tabRejected": { en: "Rejected", ta: "நிராகரிக்கப்பட்டது" },
  "dashboard.tableDate": { en: "Date", ta: "தேதி" },
  "dashboard.tableCandidate": { en: "Candidate", ta: "வேட்பாளர்" },
  "dashboard.tableCredential": { en: "Credential", ta: "சான்று" },
  "dashboard.tableOfficer": { en: "Officer", ta: "அதிகாரி" },
  "dashboard.tableDecision": { en: "Decision", ta: "முடிவு" },
  "dashboard.tableActions": { en: "Actions", ta: "செயல்கள்" },

  // ===========================================================================
  // Billing
  // ===========================================================================
  "billing.title": { en: "Billing", ta: "கட்டணம்" },
  "billing.membership": { en: "Membership", ta: "உறுப்பினர்" },
  "billing.subscription": { en: "Annual subscription", ta: "வருடாந்திர சந்தா" },
  "billing.paymentHistory": { en: "Payment history", ta: "செலுத்துகை வரலாறு" },
  "billing.noPayments": {
    en: "No payments yet.",
    ta: "இன்னும் செலுத்துகைகள் இல்லை.",
  },
  "billing.payPerYear": {
    en: "Pay {amount} / year",
    ta: "ஆண்டுக்கு {amount} செலுத்து",
  },
  "billing.opening": { en: "Opening...", ta: "திறக்கிறது..." },
  "billing.earnings": { en: "Earnings", ta: "வருவாய்" },
  "billing.totalEarned": { en: "Total earned", ta: "மொத்த வருவாய்" },
  "billing.payableBalance": {
    en: "Payable balance",
    ta: "செலுத்த வேண்டிய இருப்பு",
  },
  "billing.alreadyPaidOut": {
    en: "Already paid out",
    ta: "ஏற்கனவே செலுத்தப்பட்டது",
  },
  "billing.awaitingPayout": {
    en: "Awaiting payout",
    ta: "செலுத்துகைக்காக காத்திருக்கிறது",
  },

  // ===========================================================================
  // Nav
  // ===========================================================================
  "nav.profile": { en: "My profile", ta: "என் சுயவிவரம்" },
  "nav.verifications": { en: "Verification status", ta: "சரிபார்ப்பு நிலை" },
  "nav.billing": { en: "Payments", ta: "கட்டணங்கள்" },
  "nav.talentHub": { en: "Talent Hub", ta: "திறமையாளர்கள் மையம்" },
  "nav.dashboard": { en: "Home", ta: "முகப்பு" },
  "nav.inbox": { en: "Inbox", ta: "கோரிக்கைகள்" },
  "nav.billingEarnings": { en: "Payments", ta: "கட்டணங்கள்" },
  "nav.overview": { en: "Overview", ta: "மேலோட்டம்" },
  "nav.pendingApplications": {
    en: "Pending applications",
    ta: "நிலுவை விண்ணப்பங்கள்",
  },
  "nav.auditLog": { en: "Audit log", ta: "தணிக்கை பதிவு" },
  "nav.admin": { en: "Admin", ta: "நிர்வாகி" },
  "nav.notifications": { en: "Notifications", ta: "அறிவிப்புகள்" },
  "nav.language": { en: "Language", ta: "மொழி" },
  "nav.languageEnglish": { en: "English", ta: "ஆங்கிலம்" },
  "nav.languageTamil": { en: "Tamil", ta: "தமிழ்" },
  "nav.settings": { en: "Settings", ta: "அமைப்புகள்" },
  //For post a job opportunity
  "nav.postOpportunity": {
    en: "Post Opportunity",
    ta: "வாய்ப்பை பதிவிடுங்கள்",
  },
};
